<template>
  <div>
    <v-dialog
      v-model="showDialog"
      persistent
      max-width="500px"
    >
      <v-card>
        <v-card-title>
          Сформировать отчёт
        </v-card-title>

        <template v-if="!requested">
          <v-card-text>
            <v-select
              ref="selectReportType"
              persistent-placeholder
              :items="typesListForSelect"
              :rules="[rules.required('Необходимо выбрать')]"
              v-model="type"
              label="Тип отчёта"
              placeholder="Выберите тип отчёта"
            ></v-select>

            <div v-show="type">
              <v-date-picker
                landscape
                range
                scrollable
                full-width
                no-title
                :max="new Date().toISOString()"
                elevation="2"
                first-day-of-week="1"
                v-model="date"
              >
              </v-date-picker>

              <div v-show="date.length" class="text-center mt-2">
                Выбранный период: <span class="font-weight-bold">{{ formatReportDateCalendar(date) }}</span>
              </div>

              <div v-if="type === 'refills'">
                <v-checkbox
                  dense
                  persistent-hint
                  label="Только по определенным клиентам"
                  hint="Если Вы хотите получить отчёт по только определенным клиентам"
                  v-model="onlyExplicitClients"
                ></v-checkbox>

                <CompanySearch
                  v-show="onlyExplicitClients"
                  ref="componentCompanySearch"
                  label="Поиск по организациям"
                  @input="inputClientOgrn"
                  forPartnerForm
                  class="ms-6 mt-2"
                >
                  <template #company-not-found>
                    <p class="text-center">Мы старались, но не нашли вашу организацию ☹️</p>
                  </template>
                </CompanySearch>

                <v-list class="ms-6">
                  <v-list-item
                    v-for="item in itemsOgrns"
                    :key="item.value"
                    class="ps-0"
                  >
                    <v-list-item-action class="m-0 pl-0">
                      <v-checkbox
                        :input-value="isOgrnSelected(item.value)"
                        @change="selectOgrn(item.value, $event)"
                      >
                      </v-checkbox>
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-title>{{ item.text }}</v-list-item-title>
                      <v-list-item-subtitle>ОГРН: {{ item.value }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="secondary"
              text
              @click="showDialog = false"
            >
              Закрыть
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              :disabled="reportCanBePrepared === false"
              @click="prepareReport"
            >
              Продолжить
            </v-btn>
          </v-card-actions>
        </template>
        <template v-else>
          <v-card-text>
            <v-img
              class="mb-6 mx-auto"
              src="@/assets/img/vector.png"
              width="84px"
            ></v-img>

            <div class="text-center text-body-2">
              Отчёт будет сформирован в ближайшее время. <br />
              Он появится во вкладке "Акты и отчёты".
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="secondary"
              text
              @click="showDialog = false"
            >
              Закрыть
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <v-container fluid>
      <v-row>
        <v-spacer></v-spacer>
        <v-col cols="auto" align-self="end" class="text-right">
          <v-btn
            outlined
            color="secondary darken-3"
            @click.pre.prevent.stop="showDialog = true"
          >
            Сформировать отчёт
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-data-table
            :mobile-breakpoint="100"
            class="bidlist-table"
            :headers="headers"
            :items="reports_list"
            item-key="report_id"
          >
            <template #[`item.report_type`]="{ value }">
              <span>
                <span>{{ getReportTypeLabel(value) }}</span>
              </span>
            </template>

            <template #[`item.requested_at`]="{ value }">
              <span>{{ new Date(value).toLocaleString() }}</span>
            </template>

            <template #[`item.report_id`]="{ value, item }">
              <template v-if="item.is_generated && item.attachments">
                <AttachmentItem
                  v-for="attachment in item.attachments"
                  key="id"
                  :attachment="attachment"
                  dense
                  icon-size-class="fiv-size-md"
                  label="Отчёт"
                ></AttachmentItem>
              </template>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import {required} from "@/utils/rules";
import {validateOgrn} from "@/utils/helpers";
import {prepareReport as serverPrepareReport} from "@/api";
import {mapActions, mapState} from "vuex";
import AttachmentItem from "@/components/common/AttachmentItem";
import CompanySearch from "@/components/common/CompanySearch";

const enumList = {
  'refills': {
    text: 'Пополнения'
  },
  'payouts': {
    text: 'Выводы'
  }
};

export default {
  name: "ListReports",

  components: {
    CompanySearch,
    AttachmentItem
  },

  data() {
    return {
      type: null,
      showDialog: false,
      requested: false,
      date: [],
      onlyExplicitClients: false,
      itemsOgrns: [],
      selectedOgrnsList: [],
      typesList: {...enumList},
      typesListForSelect: Object.entries(enumList).map(([key, item]) => ({
        text: item.text,
        value: key
      })),
      headers: [{
        text: 'Тип отчёта',
        value: 'report_type'
      },{
        text: 'Дата запроса',
        value: 'requested_at'
      },{
        text: '',
        value: 'report_id',
        sortable: false,
        align: 'end',
        width: '15%'
      }],
      rules: {
        required,
        validOgrnMulti: (v) =>
        {
          if (v && Array.isArray(v) && v.length)
          {
            const item = v.find((item) => !validateOgrn(item));
            if (item) {
              return 'Введен недействильный ОГРН: ' + item;
            }
          }

          return true;
        }
      }
    }
  },

  computed: {
    ...mapState("billing", ["reports_list"]),

    reportCanBePrepared() {
      return (
        this.type !== null
        && (Array.isArray(this.date) && this.date.length > 0)
        && (
          this.onlyExplicitClients === false
          || (this.onlyExplicitClients && Array.isArray(this.itemsOgrns) && this.itemsOgrns.length)
        )
      );
    },
  },

  watch: {
    showDialog(value) {
      if (!value) {
        this.$refs.selectReportType && this.$refs.selectReportType.reset();
        this.$refs.comboboxReportClientsOgrns && this.$refs.comboboxReportClientsOgrns.reset();

        // FIXME:
        this.requested = false;
        this.type = null;
        this.itemsOgrns = [];
        this.selectedOgrnsList = [];
        this.onlyExplicitClients = false;
        this.date = [];
      }
    },

    date(value) {
      if (value && Array.isArray(value) && value.length > 1)
      {
        if (new Date(value[0]) > new Date(value[1]))
        {
          const lastDate = value[0];

          value[0] = value[1];
          value[1] = lastDate;
        }
      }
    }
  },

  methods: {
    ...mapActions("billing", ["getReportsList"]),

    formatReportDateCalendar(value) {
      if (value && Array.isArray(value) && value.length)
      {
        const firstDate = value[0];
        const secondDate = value[1];

        if (firstDate === secondDate || firstDate === (new Date().toISOString().substring(0, 10))) {
          return firstDate;
        }

        return 'с ' + value.join(' по ');
      }

      return null;
    },

    inputClientOgrn(value)
    {
      if (value?.ogrn)
      {
        if (this.itemsOgrns.findIndex(item => item.value === value.ogrn) === -1) {
          this.selectedOgrnsList.push(value.ogrn);
          this.itemsOgrns.push({
            value: value.ogrn,
            text: value.name
          });
        }

        this.$refs.componentCompanySearch.clear();
      }
    },

    async prepareReport() {
      const response = await serverPrepareReport({
        type: this.type,
        dates: this.date,
        ogrnList: this.selectedOgrnsList
      });

      if (response) {
        this.requested = true;
      }
    },

    getReportTypeLabel(type) {
      return this.typesList[type]?.text ?? 'Н/Д';
    },

    isOgrnSelected(ogrn) {
      return this.selectedOgrnsList.indexOf(ogrn) !== -1;
    },

    selectOgrn(ogrn, toggleStatus) {
      const isSelected = this.isOgrnSelected(ogrn);

      if (toggleStatus && !isSelected) {
        this.selectedOgrnsList.push(ogrn);
      } else if (!toggleStatus && isSelected) {
        this.selectedOgrnsList.splice(this.selectedOgrnsList.indexOf(ogrn), 1);
      }
    }
  }
}
</script>

<style scoped>
</style>
